import { useTranslation } from "react-i18next";
import FormLayout from "components/form/FormLayout";
import { useAppDispatch } from "hooks/redux";
import React, { useState } from "react";
import { useHistory } from "react-router";
import Input from "components/form/input/index";
import { ButtonNew as Button } from "components/buttonNew";
import "./teeOnPortalLogin.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/pro-light-svg-icons";
import validator from "validator";
import { showError, showSuccess } from "redux/actions/ui";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { PostPasswordResetCode } from "api/rpc/2022-09/passwordReset";
import { StatusCode } from "api/protocols";
import { useTeeOnPortalContext } from "../TeeOnPortalContext";
import { CreateAccount, LockTeeTimeById } from "api/rpc/2022-09/guest/bookingEngine";
import { UserLogin } from "api/rpc";
import { ITeeTime } from "redux/reducers/models/teetime";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import { useLocation } from "react-router-dom";

interface IRegisterState {
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
}

export interface IValidState {
  invalidEmail: boolean;
  invalidFirstName: boolean;
  invalidLastName: boolean;
  invalidPhoneNumber: boolean;
  invalidPassword: boolean;
  invalidPasswordMatch: boolean;
  emailInUse: boolean;
}

interface ILocationState {
  params: { golfCourses: boolean };
}

export default function TeeOnPortalRegister() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const facilityShortName = new URLSearchParams(window.location.search)?.get("facility");
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const location = useLocation();

  const [registerState, setRegisterState] = useState<IRegisterState>({
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    firstName: "",
    lastName: "",
  });

  const [validState, setValidState] = useState<IValidState>({
    invalidEmail: false,
    invalidFirstName: false,
    invalidLastName: false,
    invalidPhoneNumber: false,
    invalidPassword: false,
    invalidPasswordMatch: false,
    emailInUse: false,
  });

  function navigate(url: string, newTab = false) {
    if (newTab) {
      window.open(url);
    } else {
      history.push(url);
    }
  }

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      //void handleTeeOnPortalLogin();
    }
  }

  function navigateToLogin() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const teeTimeId = urlParams.get("id");
    const facilityShortName = urlParams.get("facility");
    if (date && teeTimeId && facilityShortName) {
      void navigate(`/booking/login?id=${teeTimeId}&date=${date}&facility=${facilityShortName}`);
    } else {
      void navigate("/booking/login");
    }
  }

  async function createAccount() {
    const params = {
      first_name: registerState.firstName ?? "",
      last_name: registerState.lastName ?? "",
      email: registerState.email ?? "",
      password: registerState.password ?? "",
      phone: registerState.phoneNumber ?? "",
    };

    if (
      params.first_name === "" ||
      params.last_name === "" ||
      params.email === "" ||
      params.password === "" ||
      params.phone === ""
    ) {
      dispatch(showError(t("Please fill out all fields to register")));
      return;
    }

    const invalidFirstName = !validator.isAlpha(params.first_name);
    const invalidLastName = !validator.isAlpha(params.last_name);
    const invalidEmail = !validator.isEmail(params.email);
    const invalidPhoneNumber = !validator.isMobilePhone(params.phone, "en-CA");
    const invalidPassword = params.password.length < 8;
    const invalidPasswordMatch = registerState.password !== registerState.confirmPassword;

    setValidState(prevState => ({
      ...prevState,
      invalidFirstName: invalidFirstName,
      invalidLastName: invalidLastName,
      invalidEmail: invalidEmail,
      invalidPhoneNumber: invalidPhoneNumber,
      invalidPassword: invalidPassword,
      invalidPasswordMatch: invalidPasswordMatch,
    }));

    if (
      invalidFirstName ||
      invalidLastName ||
      invalidEmail ||
      invalidPhoneNumber ||
      invalidPassword ||
      invalidPasswordMatch
    ) {
      if (invalidEmail) {
        setValidState(prevState => ({ ...prevState, emailInUse: false }));
      }
      return;
    }

    const accountCreationRes = await CreateAccount(params, true);

    if (accountCreationRes.status !== StatusCode.OK) {
      if (accountCreationRes.data === "The email has already been taken.") {
        setValidState(prevState => ({ ...prevState, emailInUse: true }));
      } else {
        dispatch(showError(accountCreationRes.data));
      }

      return;
    }
    dispatch(showSuccess("Account created successfully"));
    setValidState(prevState => ({ ...prevState, emailInUse: false }));
    await confirmSignIn(params);
  }

  async function confirmSignIn(registerInfo: Partial<IRegisterState>) {
    const username = registerInfo.email;
    const password = registerInfo.password;

    const params = {
      email: username,
      password: password,
    };

    //Validate user login
    const loginRes = await UserLogin(params, true);

    if (loginRes.status !== StatusCode.OK) {
      dispatch(showError("Error signing in. Please try again"));
      void navigateToLogin();
      return;
    }

    updateTeeOnPortal({ user: loginRes.data?.user });

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const teeTimeId = urlParams.get("id");
    const facilityShortName = urlParams.get("facility");

    const locationState = location.state as ILocationState;
    if (locationState?.params?.golfCourses) {
      void navigate(`/booking/teetimes/golf-courses`);
      return;
    }

    //Tee time not previously selected, send user to tee times list
    if (!date || !date || !teeTimeId || !facilityShortName) {
      void navigate("/booking/teetimes");
      return;
    }

    const facility =
      teeOnPortalState?.selectedTeeTime?.facility ??
      teeOnPortalState?.selectedFacilities?.find(facility => facility?.short_name === facilityShortName);
    let selectedTeeTime: ITeeTime & { formattedDate?: string; formattedStartTime?: string } =
      teeOnPortalState?.selectedTeeTime?.teeTime;

    const teeTimeParams = {
      id: Number(teeTimeId),
      date: date,
      facility_id: facility?.id,
      extended: true,
      turn_tee_time: true,
    };

    //Load tee time if not already in state
    if (!selectedTeeTime && facility) {
      const teeTimeParams = {
        date: date,
        id: Number(teeTimeId),
        facility_id: facility?.id,
        extended: true,
        turn_tee_time: true,
      };
      const teeTimeRes = await GetTeeTimesCustomer(teeTimeParams, true);
      if (teeTimeRes?.status !== StatusCode.OK) {
        dispatch(showError("Error getting selected tee time"));
        return;
      }
      if (teeTimeRes?.data?.length === 0) {
        dispatch(showError("Tee time is unavailable, please choose another"));
        history.push(`/booking/teetimes/${facilityShortName}`);
        return;
      }
      selectedTeeTime = teeTimeRes?.data[0];
    }

    if (selectedTeeTime?.id === Number(teeTimeId)) {
      //If not null - User has access to their own locked teetime
      if (selectedTeeTime?.lock !== null) {
        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(selectedTeeTime?.lock?.start_time) + "Z";

        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(selectedTeeTime?.lock?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            lock: { lockoutDuration, lockStartTime, expireTime: selectedTeeTime?.lock?.expire_time },
          },
        });

        history.push(`/booking/teetimes/book/id?=${teeTimeId}&date=${date}`);
      } else {
        const bookingLockRes = await LockTeeTimeById({ tee_time_id: selectedTeeTime?.id }, true);

        if (bookingLockRes.status !== StatusCode.OK) {
          dispatch(showError("Error locking tee time"));
          return;
        }

        //Locked out message
        const bookingLockMessage = "Tee time already locked by user";
        const bookingLockResMessage = bookingLockRes.data.message;

        if (bookingLockMessage === bookingLockResMessage) {
          dispatch(showError("Booking is locked by another user, please try again in a few minutes.."));
          return;
        }

        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(bookingLockRes?.data?.start_time) + "Z";
        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(bookingLockRes?.data?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            lock: { lockoutDuration, lockStartTime, expireTime: bookingLockRes?.data?.expire_time },
          },
        });
        history.push(`/booking/teetimes/book/id?=${teeTimeId}&date=${date}`);
      }
    } else {
      dispatch(showError("Tee time is unavailable, please choose another"));
      history.push(`/booking/teetimes/${facilityShortName}`);
      return;
    }
  }

  async function sendPasswordRecovery() {
    if (registerState.email === undefined || !validator.isEmail(registerState.email)) {
      dispatch(showError("Please enter a valid email for recovery"));
      return;
    }

    let facilityId = teeOnPortalState?.selectedTeeTime?.facility?.id ?? teeOnPortalState?.selectedFacilities[0]?.id;

    if (!facilityId && facilityShortName) {
      const facilityRes = await GetFacilityGuest({ short_name: facilityShortName }, true);
      if (facilityRes.status !== StatusCode.OK) {
        dispatch(showError("Error getting facility"));
        return;
      }
      facilityId = facilityRes?.data[0]?.id;
    }

    const params = {
      email: registerState.email,
      facility_id: facilityId,
    };

    const resetRes = await PostPasswordResetCode(params, true);

    if (resetRes.status !== StatusCode.OK) {
      if (typeof resetRes?.data === "string") {
        dispatch(showError(resetRes.data));
      } else {
        dispatch(showError("Error sending password reset code"));
      }
      return;
    }

    dispatch(showSuccess("Email Invite Sent"));
    void navigateToLogin();
  }

  return (
    <div className="tee-on-portal-login">
      <div className="tee-on-portal-login-top-bar">
        <div className="tee-on-portal-login-logo-container">
          <img
            className="tee-on-portal-nav-logo"
            src={"/public/images/Tee-On.png"}
            alt="Tee On Logo"
            onClick={() => navigate(`/booking/teetimes${facilityShortName ? "/".concat(facilityShortName) : ""}`)}
          />
        </div>

        <div>
          <span className="tee-on-portal-login-secondary-text">Already have an account? </span>
          <span onClick={navigateToLogin} className="tee-on-portal-login-link-text">
            Log in
          </span>
        </div>
      </div>

      <div className="tee-on-portal-login-main-container-outer">
        <div className="tee-on-portal-login-main-container">
          <div className="tee-on-portal-login-text-group">
            <h1 className="tee-on-portal-login-primary-text">Create Account</h1>
            <p className="tee-on-portal-login-secondary-text">Please sign in or create an account to book a tee time</p>
          </div>
          {validState.invalidFirstName ||
          validState.invalidLastName ||
          validState.invalidEmail ||
          validState.invalidPhoneNumber ||
          validState.invalidPassword ||
          validState.invalidPasswordMatch ||
          validState.emailInUse ? (
            <div className="register-error">
              {validState.invalidFirstName && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{"First name cannot contain numbers"}</p>
                </div>
              )}
              {validState.invalidLastName && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{"Last name cannot contain numbers"}</p>
                </div>
              )}
              {validState.invalidEmail && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{"Email is not in the correct format"}</p>
                </div>
              )}
              {validState.invalidPhoneNumber && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{"Phone number is not in the correct format"}</p>
                </div>
              )}
              {validState.invalidPassword && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{t("Password must be at least 8 characters long")}</p>
                </div>
              )}

              {validState.invalidPasswordMatch && (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                  </div>
                  <p>{t("Passwords do not match")}</p>
                </div>
              )}

              {validState.emailInUse && (
                <div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div style={{ display: "flex" }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="register-triangle-icon" />
                    </div>
                    <p>
                      {"It looks like you already have an account. Please click the"} &apos;{"Resend Invite"}
                      &apos; {"button below and check your inbox and junk folder to set a password for your account."}
                    </p>
                  </div>

                  <button onClick={sendPasswordRecovery} className="register-email-resend-invite">
                    {"RESEND INVITE"} <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>
              )}
            </div>
          ) : null}
          <FormLayout>
            <FormLayout.Group>
              <Input
                label="First Name"
                value={registerState?.firstName}
                onChange={e => setRegisterState(prevState => ({ ...prevState, firstName: e.target.value }))}
              />
              <Input
                label="Last Name"
                value={registerState?.lastName}
                onChange={e => setRegisterState(prevState => ({ ...prevState, lastName: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label="Email Address"
                value={registerState?.email}
                onChange={e => setRegisterState(prevState => ({ ...prevState, email: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Phone Number")}
                value={registerState?.phoneNumber}
                onChange={e => setRegisterState(prevState => ({ ...prevState, phoneNumber: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input.Password
                label={t("Password")}
                value={registerState.password}
                name="password"
                onKeyDown={handleEnterKeydown}
                onChange={e => setRegisterState(prevState => ({ ...prevState, password: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input.Password
                label={t("Confirm Password")}
                value={registerState.confirmPassword}
                name="confirmPassword"
                onKeyDown={handleEnterKeydown}
                onChange={e => setRegisterState(prevState => ({ ...prevState, confirmPassword: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Button type="primary" size="medium" onClick={createAccount} block>
                Create Account
              </Button>
            </FormLayout.Group>
          </FormLayout>
        </div>
      </div>
    </div>
  );
}
